import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import { loadSystemLanguage } from '@contexts/SystemLanguageContext';
import { initAnalytics } from '@cw-elements/analytics-provider';
import { datadogLogs } from '@datadog/browser-logs';
import {
  getDatadogLogsParameters,
  getDatadogRumParameters,
  setDatadogGlobalContext,
} from 'src/datadog';
import { setAnalyticsStatus } from './analyticsLifecycle';
import type { GuideIntegration } from './guides/guides.models';
import { registerGuides } from './guides/registerGuides';

/**
 * Analytics must never run in local dev or under test runners (Vitest sets NODE_ENV='test', Cypress
 * exposes globalThis.window.Cypress), so automated/dev runs don't pollute production RUM / Amplitude data.
 */
export function isAnalyticsEnabledEnvironment() {
  const isTestRunner =
    process.env.NODE_ENV === 'test' ||
    (globalThis.window !== undefined && 'Cypress' in globalThis.window);

  return process.env.NODE_ENV !== 'development' && !isTestRunner;
}

/**
 * Single consent-aware bootstrap: Amplitude + Datadog RUM both run through the wrapper, which owns
 * identity/consent and returns the Amplitude client so we can attach the Engagement plugin. Datadog
 * Logs has no wrapper support, so it stays on the direct SDK. Kicked off at module load (not in a
 * React effect) so RUM captures the authentication flow before the app routes mount.
 */
export function initObservability(guides?: GuideIntegration) {
  if (!isAnalyticsEnabledEnvironment()) {
    setAnalyticsStatus('disabled');
    return Promise.resolve();
  }

  setAnalyticsStatus('initializing');
  const ready = initAnalytics({
    amplitude: {
      clientId: import.meta.env.VITE_APP_AMPLITUDE,
      autocapture: true,
      sendIpAddressOnConsent: true,
    },
    datadog: getDatadogRumParameters(),
    consent: { enabled: true },
  })
    .then((instance) => {
      setAnalyticsStatus('ready');
      instance?.add(engagementPlugin({ locale: loadSystemLanguage() }));
      if (guides) registerGuides(guides);
      setDatadogGlobalContext();
    })
    // Analytics bootstrap must never break app startup, so callers can safely fire-and-forget.
    .catch((error) => {
      setAnalyticsStatus('disabled');
      datadogLogs.logger.error('Analytics bootstrap failed', {
        error: error instanceof Error ? error.message : String(error),
      });
    });

  datadogLogs.init(getDatadogLogsParameters());

  return ready;
}
